/**
 * The RPC wrapper — the call half of the N3 seam.
 *
 * N3 put seven ledger mutations and twelve read-only report functions in
 * Postgres (supabase/migrations/0009-0019, 0024, 0141 — see REPORT_RPCS, which
 * is the list, and says which reports are deliberately not on it). They are
 * reachable from the browser through
 * PostgREST, but src/data/client.js exports only `getSupabase` and
 * `subscribeToIdToken`, so without this file every call site would hand-roll the
 * same four things — and the first three of them are easy to get subtly wrong:
 *
 *   1. keeping the raw error intact (see PRESERVE THE ERROR below),
 *   2. classifying the refusal (src/data/reportErrors.js),
 *   3. reading a report's cache watermark in the ONLY sound order,
 *   4. a predictable `{ data, error }` shape.
 *
 * This file has no call sites yet, deliberately. It is the seam; something else
 * consumes it.
 *
 * ===========================================================================
 * NOTHING IS IMPORTED EXCEPT THE CLASSIFIER
 * ===========================================================================
 * The Supabase client is INJECTED rather than imported from ./client. That is
 * not test ceremony: this module has no opinion about how the client is built,
 * `createClient` is memoised over there for reasons that belong over there
 * (one realtime socket per tab), and injecting means every branch here is
 * reachable without Firebase, `import.meta.env`, or a server. Callers pass
 * `getSupabase()` from a provider, once.
 *
 * ===========================================================================
 * PRESERVE THE ERROR — the mistake this file exists to make unrepresentable
 * ===========================================================================
 * src/data/useOrgRefdata.js:11-20 spells it out: building a plain Error from
 * `${code}: ${message}` throws the code away as a PROPERTY and leaves it only as
 * text, which is fatal for src/data/accessGuard.js, whose rule is "SQLSTATE
 * 42501 AND the not_a_member sentinel". A guard reading a flattened message
 * cannot tell a revoked member from an anonymous caller, and that misreading
 * fires session teardown on healthy sessions.
 *
 * So `rpc()` RESOLVES with `{ data, error }` and never rejects for a server
 * failure — the same shape supabase-js itself returns, for the same reason. The
 * classified verdict carries the untouched error object as `.raw`, and
 * `onAccessError` receives that raw object BEFORE any classification, exactly as
 * useOrgRefdata does, so the access guard always sees what the server actually
 * said. `rpcError()` is available for callers that genuinely need a rejection
 * (react-query, an error boundary) and it keeps `.code`, `.verdict` and `.cause`
 * rather than flattening.
 *
 * ===========================================================================
 * THE CACHE CONTRACT — why `readReport` reads the watermark FIRST
 * ===========================================================================
 * 0015_n3_ledger_watermark.sql replaces TTL caching with an equality:
 *
 *     a report computed at watermark W is valid for exactly as long as
 *     ledger_watermark(org) returns W
 *
 * That holds because every ledger mutation appends its `org_events` row inside
 * the SAME TRANSACTION as the write (0009-0011), so a ledger row cannot be
 * visible to a reader whose watermark read missed the event.
 *
 * The order of the two reads is the whole ballgame, and one order is unsound:
 *
 *   report first, then watermark  →  a mutation that lands between them is
 *     absent from the answer but PRESENT in the watermark. The answer is then
 *     labelled with a watermark it does not correspond to, the equality check
 *     passes, and the cache serves a stale report FOREVER — there is no TTL left
 *     to rescue it. This is the failure the whole design is meant to remove.
 *
 *   watermark first, then report  →  sound. Proof: the report reflects some
 *     state S with w(S) >= W (the watermark only advances). We cache (answer, W).
 *     Later we serve it only when the current watermark C equals W. Monotonicity
 *     gives W <= w(S) <= C = W, so w(S) = W exactly: the cached answer IS the
 *     state at W. A mutation during the report makes C > W and the entry is
 *     simply never served — which is the correct outcome, not a lost hit.
 *
 * `readReport` therefore does the reads in that order and hands back
 * `{ data, error, watermark, access, cacheKey }`. There is NO TTL anywhere in
 * this file, and no cache either: the caller owns storage. Store
 * `cacheKey -> { watermark, data }` and serve when
 * `entry.watermark === (await ledgerWatermark(org)).data`.
 *
 * The watermark is deliberately NOT part of `cacheKey`. The key identifies the
 * QUESTION and the watermark validates the ANSWER; folding the watermark in
 * would mint a fresh key on every mutation and grow the map with entries that
 * can never be hit again.
 *
 * ===========================================================================
 * A BUDGET REPORT HAS TWO INPUTS, SO IT HAS TWO VALIDATORS (0247, GH#562)
 * ===========================================================================
 * `budget_to_actual` and `yoy_budget_execution` read the ledger AND
 * `budget_lines`, and the two move independently: publishing a budget changes
 * the BUDGET column while every ACTUAL stays put. 0064 and 0140 both refused to
 * widen `ledger_watermark` to cover it — that would invalidate every cached
 * balance sheet in the org for a change that cannot move a balance sheet — so
 * these reports had no key at all and went through the bare `rpc()` uncached.
 *
 * 0247 supplies the second key: `budget_watermark(org, budget_ids)` is
 * `max(org_events.seq)` over `t3.budget_watermark_topics()`, which since 0250 is
 * `budget.published` alone — emitted by `publish_budget` inside the same
 * transaction as the write. The other budget topic, `budget.changed`, is emitted
 * only by the three DRAFT writers, and `budgets_for_range` reads published rows
 * only, so a draft edit changes no budget report and must not evict one. The
 * proof above is the same proof, once per validator — both are read BEFORE the
 * report, both are stored beside the answer, and a hit requires BOTH to still
 * match. `runner.js` does that; the equality is per validator, so a stale answer
 * needs both to be unchanged, which is exactly when it is not stale.
 *
 * `budget_watermark` is NOT folded into the key for the same reason the ledger
 * watermark is not: it validates the answer, it does not identify the question.
 * It is read ORG-WIDE. `p_budget_ids` exists and nothing passes it: a shaper's
 * `plan()` runs before `budgets_for_range` answers, so it cannot know which
 * budget documents its own report will resolve, and `runner.js`'s header carries
 * the two reasons a narrowed probe would not be sound even if it could.
 *
 * ===========================================================================
 * THE OTHER HALF OF THE KEY — WHO IS ASKING
 * ===========================================================================
 * The watermark answers "have the rows changed". It cannot answer "have the rows
 * I AM ALLOWED TO SEE changed", and that is a different question here by design:
 * the report functions are not SECURITY DEFINER, so `journal_lines` is filtered
 * by the caller's own fund grants (0001_schema.sql's `org_member_all`).
 *
 * So `org | fn | args` was the wrong key and an org-wide watermark was the wrong
 * validator, in two directions at once. Two members of one org — one unscoped,
 * one scoped to a fund — asking the identical question shared one entry and had
 * two different CORRECT answers. And revoking a `member_fund_grants` row emits no
 * ledger event, so the watermark did not move and the member kept the
 * pre-revocation report for as long as the entry lived, which with no TTL is
 * forever.
 *
 * `readReport` therefore also reads `report_access_token(org)` (0140) — a digest
 * of the caller's identity, unscoped membership and exact grant set — and folds
 * it into the KEY. One read fixes both: two principals cannot collide because
 * their keys differ, and a revocation moves that member to a new key, so the old
 * entry is unreachable rather than needing an invalidation nobody would send.
 *
 * A watermark of 0 is a legitimate value, not an error: a brand-new org has no
 * ledger events, and so does an org whose events the caller cannot read
 * (`ledger_watermark` is a plain function, so `org_events` RLS applies). Both
 * cache correctly, and being granted access advances the watermark, which
 * invalidates the entry on its own.
 */
import { classifyRpcError, reportIfSystemFault } from "./reportErrors.js";

/**
 * The read-only report functions (0012-0019, 0024, 0141). `readReport` accepts
 * only these.
 *
 * That is a guard, not bookkeeping: `readReport("post_entry", …)` would POST an
 * entry and then cache the result under a watermark, and the misuse is one
 * plausible typo away from a screen that writes to the ledger on render.
 *
 * IT IS ALSO THE LIST THE CACHE CONTRACT APPLIES TO, so a report missing from it
 * is not merely unguarded — it cannot reach the seam at all. The list stopped at
 * 0017 while 0019 shipped five more report types and 0024 shipped the paged
 * general ledger, so `readReport("dashboard_data", …)` threw and the dashboard,
 * the account-balance panel, the credit-card panel, both custom-report modes and
 * the paged ledger all had to fall back to a bare `rpc()` — losing the watermark
 * they were the point of.
 *
 * THE BUDGET REPORTS ARE HERE NOW, and their validity key is the PAIR
 * `(ledger_watermark, budget_watermark)` — never the ledger watermark alone.
 * They were absent for as long as there was no second key: they read
 * `budget_lines` as well as the ledger, publishing a budget does not advance
 * `ledger_watermark` (0064's header, 0140's exclusion list), and listing them
 * against that watermark alone would have cached them against a validator that
 * cannot see half their input — the exact failure the watermark exists to
 * remove. 0247 supplies the missing half; `runner.js` reads both and serves a
 * hit only when both match. A caller reaching one of these through
 * `readReport` — which reads only the ledger watermark — would reintroduce the
 * original defect, so `readReport` refuses them by name.
 *
 * `yoy_budget_execution_by_fund` is NOT here and never was a function:
 * `by_fund` is a boolean parameter on both (`p_by_fund`, 0026:77-91, :132-152,
 * verified against hosted `pg_proc`). This comment used to name it.
 */
export const REPORT_RPCS = Object.freeze([
  "account_balances_as_of",
  "account_balance_sides_as_of",
  "trial_balance_as_of",
  "account_activity_between",
  "calc_report",
  "general_ledger_rows",
  "general_ledger_page",
  "dashboard_data",
  "account_balance_for_date",
  "credit_card_fund_balances",
  "custom_report_summary",
  "custom_report_comparison",
  // 0240 — the newest journal line on one account as of a date, and how many
  // lines fall in the window. `handleAccountBalance` got both from the same
  // BigQuery scan that gave it the balance; Postgres had no analogue at all.
  // Ledger-watermarked like every other entry here: it reads `journal_lines`
  // and nothing else, so a ledger mutation is exactly what invalidates it.
  "account_last_transaction",
  // 0243 — the income statement's byFund Sankey (`_fundTransfers`). It reads
  // journal_lines and nothing else, so a ledger mutation is exactly what
  // invalidates it. Named here for the guard as much as the cache: the legacy
  // helper returns an EMPTY result rather than an error whenever it cannot
  // answer (fundTransfersHelper.js:58-60), so a call that never reached the
  // seam would look identical to a period with no transfers.
  "fund_transfers_between",
  // 0244 — the balance sheet with a period axis (columnsMode:"period"), one
  // cumulative pass instead of N x calc_report. Same watermark, same inputs.
  "balance_sheet_periods",
  // 0248 — the three budget report types. THEIR VALIDITY KEY IS THE PAIR
  // `(ledger_watermark, budget_watermark)`: they read the ledger through
  // calc_report AND `budget_lines`, and the two move independently. Membership
  // of THIS list is what makes `runner.js` read the second validator alongside
  // the first (see BUDGET_WATERMARK_ONLY below) — derived from the primitives a
  // plan names, never declared by the shaper.
  "budgets_for_range",
  "budget_to_actual",
  "yoy_budget_execution",
]);

/**
 * The subset of `REPORT_RPCS` whose answer the ledger watermark alone cannot
 * validate — they read `budget_lines`, which no ledger event describes.
 *
 * Exported so the refusal in `readReport` is a lookup rather than three names
 * repeated, and so a test can assert the two lists have not drifted apart. A
 * report added to `REPORT_RPCS` that reads budgets and is NOT named here is the
 * defect this whole pair of keys exists to prevent, and it is invisible: it
 * caches, it hits, and it serves last month's budget.
 */
export const BUDGET_REPORT_RPCS = Object.freeze([
  "budgets_for_range",
  "budget_to_actual",
  "yoy_budget_execution",
]);

/** The mutating ledger functions (0009-0011). Every one accepts `p_request_id`. */
export const LEDGER_RPCS = Object.freeze([
  "post_entry",
  "amend_entry",
  "annotate_entry",
  "void_entry",
  "delete_entry",
  "attach_receipt",
  "remove_receipt",
  "set_starting_balance",
  "clear_starting_balance",
]);

/** The cache-key functions (0015, 0140, 0247). Neither reports nor mutations. */
export const WATERMARK_RPC = "ledger_watermark";
export const ACCESS_TOKEN_RPC = "report_access_token";
export const BUDGET_WATERMARK_RPC = "budget_watermark";

/**
 * An idempotency key for one mutation attempt.
 *
 * Lives here rather than in a door module because BOTH posting doors need it and
 * both already import this file — the manual ledger and the bank-feed tray. It
 * was written twice, identically, which is how two generators drift into two
 * formats and a retry stops matching its original.
 *
 * `crypto.randomUUID` where available; the fallback is time plus randomness,
 * which is weaker but still unique enough for a per-caller key.
 */
export const newRequestId = () =>
  (globalThis.crypto?.randomUUID?.() ??
    `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`);

/**
 * Stable stringification for the cache key.
 *
 * `JSON.stringify` is key-order dependent, so the same report requested from two
 * code paths that happen to build their argument object in different orders
 * would occupy two cache entries and miss each other. Sorting makes the key a
 * function of the QUESTION rather than of how it was typed. One level deep is
 * enough: every RPC argument here is a scalar or an array of ids, and array
 * order is meaningful (it is a selection the caller chose).
 */
const stableArgs = (args) =>
  JSON.stringify(
    Object.keys(args ?? {})
      .sort()
      .map((k) => [k, args[k]]),
  );

/**
 * The cache key for one report question: org + PRINCIPAL + function + arguments.
 * Exported because a caller that builds its own key will eventually build a
 * weaker one (org + function, dropping the date range), and that key silently
 * serves August's numbers for September.
 *
 * THE PRINCIPAL IS PART OF THE QUESTION, not decoration. The report functions
 * are deliberately not SECURITY DEFINER, so `journal_lines` is filtered by the
 * caller's own fund grants — two members of one org asking the identical
 * question have two different CORRECT answers. `readReport` supplies
 * `report_access_token(org)` (0140) here: a digest of the caller's identity,
 * unscoped membership and exact grant set. That fixes both halves of the problem
 * with one read — two principals cannot collide because their keys differ, and
 * revoking a fund grant moves that member to a new key, so the pre-revocation
 * entry is never served again even though no ledger event was emitted for the
 * watermark to notice.
 *
 * The default of `""` means "a cache with exactly one principal in it", which is
 * true of a per-tab map in a signed-in session and is NOT true of anything
 * server-side. A caller building its own keys must pass one.
 *
 * Residual, stated rather than hidden: a grant change that commits WHILE a
 * report is running can label that answer with the pre-change token. It heals on
 * its own, because the principal's token has moved and the mislabelled entry is
 * unreachable from then on — unless the grant is revoked and re-granted to
 * exactly the previous set, which restores both the token and the visibility it
 * describes.
 */
// The delimiter is a printable "|", not a NUL. A NUL separator is unambiguous
// and costs two real things: it makes this file BINARY to grep, eslint and every
// other line-oriented tool, and PostgreSQL rejects NUL bytes in `text` values
// outright — so the moment a caller persists a cache key server-side it throws.
//
// Unambiguity comes from ESCAPING the two free-form components instead. It used
// to come from an argument — "an org id is a Firestore document id and a
// function name matches [a-z_]" — which held for the components that existed
// then. `principal` is supplied by the caller, so `("a|b", … , "c")` and
// `("a", … , "b|c")` would have produced the same key: two different principals'
// answers in one cache entry, which is the exact failure the principal was added
// to prevent. encodeURIComponent leaves every real value (a Firestore id, an md5
// digest) byte-identical and makes the collision unrepresentable rather than
// improbable.
export const reportCacheKey = (orgId, fn, args, principal = "") =>
  `${encodeURIComponent(orgId ?? "")}|${encodeURIComponent(principal ?? "")}` +
  `|${fn}|${stableArgs(args)}`;

/**
 * Turn a classified verdict into an Error, for callers that need a rejection.
 *
 * Mirrors `rpcFailure` in src/data/useOrgRefdata.js — same `${code}: ${message}`
 * text, same `.code`, same `.cause` — and adds `.verdict` so nothing downstream
 * has to re-classify. Note that the Error's MESSAGE is prefixed and therefore no
 * longer starts with the sentinel: anything classifying must use `.verdict` or
 * `.cause`, never `error.message`.
 */
export function rpcError(v) {
  const err = new Error(`${v?.sqlstate ?? v?.code ?? "error"}: ${v?.detail ?? "unknown"}`);
  err.code = v?.sqlstate ?? null;
  err.verdict = v ?? null;
  err.cause = v?.raw;
  return err;
}

/**
 * A verdict → a NAMED Error whose message is the verdict's own user-readable
 * sentence (or `fallback` when there is no verdict). The write doors
 * (ledgerWrites, reconciliationMarks) throw these so a screen can branch on
 * `.name` and show `.message` verbatim; `rpcError` above is the developer
 * shape (`sqlstate: detail`). Same `.code` / `.verdict` / `.cause` as rpcError.
 */
export function namedRpcError(v, { name, fallback } = {}) {
  const err = new Error(v?.message || fallback || "The request could not be completed. Please try again.");
  if (name) err.name = name;
  err.code = v?.code ?? v?.sqlstate ?? null;
  err.verdict = v ?? null;
  if (v?.raw) err.cause = v.raw;
  return err;
}

/**
 * @param {object} opts
 * @param {{rpc: Function}} opts.supabase  The memoised client, from
 *   `getSupabase()`. Required — see NOTHING IS IMPORTED above.
 * @param {(rawError: unknown, orgId: string|null) => void} [opts.onAccessError]
 *   Handed the UNTOUCHED error before it is classified, so an access guard can
 *   apply its own measured rule. Same seam useOrgRefdata already uses.
 * @param {{basis?: string}} [opts.classifyContext] Forwarded to the classifier;
 *   supply `{ basis }` when this session holds the org by an admin control
 *   session, so a membership refusal reads as "support session ended".
 */
export function createRpc({ supabase, onAccessError, classifyContext } = {}) {
  if (!supabase?.rpc) {
    // Thrown at construction, not at the first call: a provider wired up wrong
    // should fail while someone is looking at it, not on a user's first save.
    throw new Error("createRpc requires a Supabase client — pass getSupabase()");
  }

  /**
   * Call one Postgres function.
   *
   * Never rejects for a server, refusal or network failure — those come back as
   * a classified `error`. The ONLY rejection is programmer error (a missing
   * function name), which must not be dressed up as a server verdict: a caller
   * would render it to a user as if the books had refused something.
   *
   * @param {string} fn
   * @param {object} [args] The `p_*` arguments, exactly as the migration names
   *   them. Not renamed or camelCased here: PostgREST matches parameters by
   *   name, and a translation layer would turn a rename in SQL into a silent
   *   "function not found" instead of a compile-time-visible mismatch.
   * @param {{orgId?: string|null, extra?: object}} [opts] `orgId` is only used
   *   to tag `onAccessError`; it defaults to `args.p_org_id`, which every
   *   org-scoped RPC in 0009-0017 carries. `extra` is optional caller context
   *   for the Sentry event — see THE CALLER'S OWN CONTEXT below.
   * @returns {Promise<{data: unknown, error: object|null, requestId: string|null}>}
   *   `requestId` is `args.p_request_id` — or `args.p_delta_id` — handed
   *   straight back; see THE REQUEST ID COMES BACK OUT below.
   */
  async function rpc(fn, args = {}, { orgId, extra } = {}) {
    if (!fn) throw new Error("rpc requires a function name");
    const forOrg = orgId ?? args?.p_org_id ?? null;

    // ===================================================================
    // THE REQUEST ID COMES BACK OUT
    // ===================================================================
    // Every mutation mints a `p_request_id` (newRequestId, above), the server
    // persists it on `entry_versions.request_id`, and `org_event_dead_letters`
    // carries a `sentry_event_id` column meant to be joined against it. All of
    // that is useless if the client throws the key away the moment it is sent.
    //
    // It is returned on BOTH branches, not just the failing one, because the
    // question it answers is asked after a failure about a call the caller can
    // no longer see the arguments of: "I clicked Save and it errored — did it
    // post?" is settled by `SELECT * FROM entry_versions WHERE request_id = …`,
    // and by re-sending this SAME key rather than a fresh one (0009: same key +
    // same payload replays the original; a fresh key double-posts).
    //
    // `p_delta_id` is the FALLBACK, not a second concept. The N3 ledger doors
    // all declare `p_request_id` (0009); NONE of the budget doors does, and
    // PostgREST matches parameters by name, so sending one to `publish_budget`
    // is "could not find the function" rather than a stricter call. The one slot
    // that exists over there is `set_budget_cells`' `p_delta_id` — the key that
    // makes one user action ONE `budget_edits` delta across many rows
    // (0064:343-347) — and it answers the same question a request id answers,
    // through a different table: "I clicked Save and it errored; what landed?"
    // is `SELECT * FROM budget_edits WHERE delta_id = …`. Without this line a
    // budget fault reached Sentry with `request_id: null` while the id was
    // sitting in the arguments of the very call being reported.
    //
    // A no-op for every existing caller: `??` only reaches the second operand
    // when `p_request_id` is absent or null, and no RPC declares both.
    const requestId = args?.p_request_id ?? args?.p_delta_id ?? null;
    const startedAt = Date.now();

    let result;
    try {
      result = await supabase.rpc(fn, args);
    } catch (thrown) {
      // supabase-js reports a dead socket as `{ code: "", message: "TypeError:
      // fetch failed" }` rather than rejecting (measured — accessGuard's table),
      // but a client configured with throwOnError, or a fetch implementation
      // that is simply missing, would reject. Catching keeps this function's one
      // promise — "it resolves" — actually true.
      result = { data: null, error: thrown };
    }

    const { data = null, error = null } = result ?? {};
    if (!error) return { data, error: null, requestId };

    // Raw first, always. Only the caller's guard can tell revocation from an
    // expired token, and it needs the untouched code (useOrgRefdata.js:11-20).
    onAccessError?.(error, forOrg);
    const verdict = classifyRpcError(error, classifyContext);

    // ===================================================================
    // THE CALLER'S OWN CONTEXT
    // ===================================================================
    // `extra` is the one thing this seam cannot derive. Everything above it —
    // org, request id, function name, duration — is knowable from the call
    // itself, which is why it is gathered here and no call site has to
    // remember. The id of the BUDGET a failing `set_budget_cells` was editing is
    // not: it is a `p_*` argument this module deliberately refuses to interpret
    // (PostgREST matches by name, and a wrapper that reached into arguments
    // by name would have to be taught every door).
    //
    // So a caller that knows something an engineer would want on the event
    // passes it, and it is merged into the event's `context` — never its tags.
    // Tags are indexed and are a cardinality budget (`telemetry.js`'s reason 2);
    // a budget id is a needle you read off ONE event, not a facet you group
    // thousands by.
    //
    // Optional, and absent by default: every existing `{ orgId }` caller emits
    // exactly the event it emitted before.
    reportIfSystemFault(verdict, {
      orgId: forOrg,
      requestId,
      fn,
      durationMs: Date.now() - startedAt,
      extra,
    });

    return { data: null, error: verdict, requestId };
  }

  /**
   * The org's ledger watermark (0015). A cheap index probe by design — a cache
   * check must cost far less than the report it guards.
   *
   * Coerced to a Number: PostgREST returns `bigint` as a JSON number, and
   * `org_events.seq` is a per-org counter that will not approach 2^53. Keeping
   * it a number means callers can compare with `===` instead of discovering that
   * `"41" !== 41` only once a cache silently stops hitting.
   */
  async function ledgerWatermark(orgId) {
    const { data, error } = await rpc(WATERMARK_RPC, { p_org_id: orgId }, { orgId });
    if (error) return { data: null, error };
    return { data: Number(data ?? 0), error: null };
  }

  /**
   * The org's BUDGET watermark (0247) — the second validator, for the reports
   * that read `budget_lines` as well as the ledger.
   *
   * `budgetIds` would narrow it to the budgets the answer depends on. NOTHING ON
   * THIS PATH PASSES ONE — `runner.js`'s header carries why the narrowing is not
   * sound yet — so every caller gets the org-wide value: over-invalidating,
   * never stale, and the honest answer when the caller has not resolved which
   * budgets its report reads.
   *
   * Coerced to a Number for the same reason `ledgerWatermark` is — both read
   * `org_events.seq`, PostgREST returns `bigint` as a JSON number, and `"41" !==
   * 41` would show up only as a cache that silently stopped hitting.
   */
  async function budgetWatermark(orgId, budgetIds = null) {
    const { data, error } = await rpc(
      BUDGET_WATERMARK_RPC,
      { p_org_id: orgId, p_budget_ids: budgetIds ?? null },
      { orgId },
    );
    if (error) return { data: null, error };
    return { data: Number(data ?? 0), error: null };
  }

  /**
   * The caller's access token for one org (0140) — the principal half of a
   * report cache key. A string; there is no meaningful arithmetic on it, only
   * equality, which is why it is a digest rather than a counter.
   */
  async function accessToken(orgId) {
    const { data, error } = await rpc(ACCESS_TOKEN_RPC, { p_org_id: orgId }, { orgId });
    if (error) return { data: null, error };
    return { data: data == null ? "" : String(data), error: null };
  }

  /**
   * Read a report together with the watermark it is valid at.
   *
   * The watermark is read FIRST — that ordering is the soundness argument, and
   * it is in the file header rather than here because it is the reason this
   * function exists at all.
   *
   * @returns {Promise<{data, error, watermark: number|null, access: string|null,
   *   cacheKey: string|null}>} On failure `watermark` is null, which is what
   *   makes "cache only what has a watermark" the natural way to write the
   *   caller. `cacheKey` is null when the principal could not be established,
   *   because a key without it is the weak key this file exists to prevent.
   */
  async function readReport(fn, args = {}, { orgId } = {}) {
    if (!REPORT_RPCS.includes(fn)) {
      // Programmer error, so it REJECTS rather than returning a verdict — and
      // it does so before the call, because a mutation reaching this path would
      // already have run by the time anyone read the result.
      throw new Error(`readReport is for report functions only, not "${fn}"`);
    }
    if (BUDGET_REPORT_RPCS.includes(fn)) {
      // This function reads ONE watermark, and a budget report needs two. A
      // caller allowed through here would cache a budget answer against a
      // validator that cannot see `budget_lines` — it would hit, and it would
      // serve last month's budget with this month's actuals beside it, which is
      // the failure the second key was built to remove. `runner.js` reads both.
      throw new Error(
        `readReport cannot validate "${fn}": its key is (ledger_watermark, ` +
          "budget_watermark) and this reads only the first. Use runReport, which " +
          "reads both.",
      );
    }
    const forOrg = orgId ?? args?.p_org_id ?? null;

    const mark = await ledgerWatermark(forOrg);
    if (mark.error) {
      // No key either: a key without the principal in it is the weak key this
      // whole mechanism exists to stop a caller from building.
      return { data: null, error: mark.error, watermark: null, access: null, cacheKey: null };
    }

    const token = await accessToken(forOrg);
    if (token.error) {
      return { data: null, error: token.error, watermark: null, access: null, cacheKey: null };
    }

    const cacheKey = reportCacheKey(forOrg, fn, args, token.data);

    const { data, error } = await rpc(fn, args, { orgId: forOrg });
    if (error) return { data: null, error, watermark: null, access: token.data, cacheKey };

    return { data, error: null, watermark: mark.data, access: token.data, cacheKey };
  }

  return { rpc, ledgerWatermark, budgetWatermark, accessToken, readReport };
}

export default createRpc;
